import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight,
  CheckCircle2,
  Circle,
  Play,
  RotateCcw,
  AlertCircle,
  PartyPopper,
  ArrowRight,
  RefreshCcw,
  Lock,
  Loader2,
  Rocket
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import { formatErrorDetail } from '../../utils/formatError';

// Stage order - the correct sequence matching Build Brief §9
// Internal IDs remain for backward compatibility, display shows 1-9
// routeNum = old URL param for /stage/:num routing
const STAGE_ORDER = [
  { id: "stage00", num: "1", routeNum: "00", name: "Assessment" },
  { id: "stage0", num: "2", routeNum: "0", name: "Idea Generation" },
  { id: "stage1", num: "3", routeNum: "1", name: "Validation" },
  { id: "stage2", num: "4", routeNum: "2", name: "Business Setup" },
  { id: "stage3", num: "5", routeNum: "3", name: "Asset Foundation" },
  { id: "stage4", num: "6", routeNum: "4", name: "Fundraising" },
  { id: "stage5", num: "7", routeNum: "5", name: "Go to Market" },
  { id: "stage6", num: "8", routeNum: "6", name: "Advisory & Structure" },
  { id: "stage7", num: "9", routeNum: "7", name: "Scaling" }
];

const Stages = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [currentStage, setCurrentStage] = useState("stage00");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Pathway stage gating — filter visible stages
  const pathwayStages = user?.pathway_stages || [];
  const VISIBLE_STAGES = STAGE_ORDER.filter(s => 
    s.id === 'stage00' || pathwayStages.includes(s.id)
  );

  // Modal states
  const [showCongrats, setShowCongrats] = useState(false);
  const [showResetAll, setShowResetAll] = useState(false);
  const [showRestartStage, setShowRestartStage] = useState(false);
  const [selectedStageToRestart, setSelectedStageToRestart] = useState(null);
  const [completedStageInfo, setCompletedStageInfo] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      const response = await axios.get(`${API}/programme/stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStages(response.data.stages);
      setCurrentStage(response.data.current_stage);
    } catch (error) {
      console.error('Error fetching stages:', error);
      toast.error('Failed to load stages');
    }
    setLoading(false);
  };

  const getStageStatus = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.status || 'locked';
  };

  // Start working on a stage
  const handleStartStage = (routeNum) => {
    // Stage 1 (Assessment / stage00) uses the new Stage 1 Assessment page
    if (routeNum === '00') {
      navigate('/stage1-assessment');
    } else {
      navigate(`/stage/${routeNum}`);
    }
  };

  // Complete current stage and move to next
  const handleCompleteStage = async (stageId) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/programme/complete-stage`, 
        { stage_id: stageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Find stage info for the modal
      const completedIdx = STAGE_ORDER.findIndex(s => s.id === stageId);
      const nextStage = completedIdx < STAGE_ORDER.length - 1 ? STAGE_ORDER[completedIdx + 1] : null;
      
      setCompletedStageInfo({
        completed: STAGE_ORDER[completedIdx],
        next: nextStage,
        isComplete: response.data.is_complete
      });
      setShowCongrats(true);
      
      fetchStages();
    } catch (error) {
      toast.error(formatErrorDetail(error.response?.data?.detail) || 'Failed to complete stage');
    }
    setActionLoading(false);
  };

  // Go to next stage from congrats modal
  const handleContinueToNext = () => {
    setShowCongrats(false);
    if (completedStageInfo?.next) {
      handleStartStage(completedStageInfo.next.routeNum);
    }
  };

  // Open restart stage confirmation
  const handleOpenRestartStage = (stage) => {
    setSelectedStageToRestart(stage);
    setShowRestartStage(true);
  };

  // Restart from a specific stage (resets all after it)
  const handleRestartStage = async () => {
    if (!selectedStageToRestart) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/programme/restart-stage`, 
        { stage_id: selectedStageToRestart.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Restarting from Stage ${selectedStageToRestart.num}`);
      setShowRestartStage(false);
      setSelectedStageToRestart(null);
      fetchStages();
    } catch (error) {
      toast.error('Failed to restart stage');
    }
    setActionLoading(false);
  };

  // Reset everything back to Stage 00
  const handleResetAll = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/programme/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Starting fresh from the beginning!');
      setShowResetAll(false);
      fetchStages();
    } catch (error) {
      toast.error('Failed to reset progress');
    }
    setActionLoading(false);
  };

  const completedCount = stages.filter(s => s.status === 'completed').length;
  const progressPercent = Math.round((completedCount / VISIBLE_STAGES.length) * 100);
  const stagesUnlocked = user?.stages_unlocked;

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/create-checkout`, {
        package_id: pathwayPackageId,
        origin_url: origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  // A stage is payment-locked if it's not stage00 AND user hasn't paid
  const isPaymentLocked = (stageId) => stageId !== 'stage00' && !stagesUnlocked;

  // Pathway label for the banner
  const pathwayNames = { A: 'Founders Track', B: 'Employment Track', C: 'Mindset Discovery' };
  const userPathway = user?.pathway;
  const pathwayName = pathwayNames[userPathway] || 'your pathway';
  const pathwayPackageId = userPathway === 'A' ? 'pathway_a' : userPathway === 'B' ? 'pathway_b' : userPathway === 'C' ? 'pathway_c' : 'pathway_a';

  // Continuation offer — detect when B/C user has completed their final pathway stage
  const paidStages = VISIBLE_STAGES.filter(s => s.id !== 'stage00'); // exclude assessment
  const allPaidComplete = paidStages.length > 0 && paidStages.every(s => getStageStatus(s.id) === 'completed');
  const showContinuationOffer = stagesUnlocked && allPaidComplete && (userPathway === 'B' || userPathway === 'C');
  const upgradeLabel = userPathway === 'B' ? 'Stages 3–9' : userPathway === 'C' ? 'Stages 5–9' : '';

  const handleUpgrade = async (demo = false) => {
    setUpgradeLoading(true);
    try {
      if (demo) {
        await axios.post(`${API}/payments/demo-upgrade`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Upgraded to Founders Track!');
        window.location.reload();
      } else {
        const origin = window.location.origin;
        const res = await axios.post(`${API}/payments/create-checkout`, {
          package_id: 'pathway_a',
          origin_url: origin,
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.url) window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || 'Upgrade failed');
    }
    setUpgradeLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-black/40">Loading your journey...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl headline text-black mb-2">My Journey</h1>
          <p className="text-black/60 body-text">
            Your path from discovery to scaling your business.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowResetAll(true)}
          className="rounded-none border-border text-black/60 hover:text-black"
          data-testid="reset-all-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Start from Beginning
        </Button>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-none p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="subhead text-black">Programme Progress</h2>
            <p className="text-sm text-black/50 mt-1">
              {completedCount} of {VISIBLE_STAGES.length} stages completed
            </p>
          </div>
          <span className="text-3xl headline text-black">{progressPercent}%</span>
        </div>
        <div className="h-3 bg-secondary rounded-none overflow-hidden">
          <div 
            className="h-full bg-primary rounded-none transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Payment Gate Banner */}
      {!stagesUnlocked && userPathway && (
        <div
          className="bg-[#1E491A] rounded-none p-6 border border-[#333]"
          data-testid="payment-gate-banner"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Lock className="h-4 w-4 text-[#F6ADBC]" />
                <h3 className="font-semibold text-white text-sm">Pathway {userPathway}: {pathwayName}</h3>
              </div>
              <p className="text-white/60 text-sm">
                Unlock your pathway to continue. Pricing is $TBA.
              </p>
            </div>
            <div className="flex gap-3 items-center flex-wrap">
              <button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                data-testid="payment-gate-unlock-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: '#F6ADBC', color: '#1E491A', border: 'none',
                  padding: '12px 24px', borderRadius: 0,
                  fontSize: '14px', fontWeight: 700, cursor: checkoutLoading ? 'wait' : 'pointer',
                  transition: 'transform 0.15s',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { if (!checkoutLoading) e.target.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.target.style.transform = ''; }}
              >
                {checkoutLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>Unlock — $TBA</>
                )}
              </button>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/payments/demo-bypass`, { package_id: pathwayPackageId }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success('Demo: Pathway unlocked');
                    window.location.reload();
                  } catch (e) { toast.error('Failed'); }
                }}
                data-testid="payment-gate-demo-bypass-btn"
                style={{
                  background: 'none', border: '1px solid #555', color: '#aaa',
                  padding: '10px 18px', borderRadius: 0,
                  fontSize: '12px', cursor: 'pointer', flexShrink: 0,
                }}
              >
                Skip (Demo)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stages List */}
      <div className="space-y-3">
        {VISIBLE_STAGES.map((stage) => {
          const status = getStageStatus(stage.id);
          const isCompleted = status === 'completed';
          const isCurrent = status === 'in_progress';
          const isLocked = status === 'locked';
          const payLocked = isPaymentLocked(stage.id);
          
          return (
            <div
              key={stage.id}
              className={`bg-white rounded-none border p-5 transition-all ${
                payLocked ? 'border-border opacity-50' :
                isCurrent ? 'border-primary ' : 
                isCompleted ? 'border-green-200' : 'border-border opacity-60'
              }`}
              data-testid={`stage-${stage.id}`}
            >
              <div className="flex items-center gap-4">
                {/* Status Icon */}
                <div className={`w-12 h-12 rounded-none flex items-center justify-center flex-shrink-0 ${
                  payLocked ? 'bg-[#f0f0f0]' :
                  isCompleted ? 'bg-[rgba(255,192,0,0.15)]' :
                  isCurrent ? 'bg-primary' :
                  'bg-secondary'
                }`}>
                  {payLocked ? (
                    <Lock className="h-5 w-5 text-black/30" />
                  ) : isCompleted ? (
                    <CheckCircle2 className="h-6 w-6 text-[#1E491A]" />
                  ) : isCurrent ? (
                    <span className="text-lg font-bold text-white">{stage.num}</span>
                  ) : (
                    <Circle className="h-5 w-5 text-black/30" />
                  )}
                </div>

                {/* Stage Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-black/40 font-medium">STAGE {stage.num}</span>
                    {isCompleted && !payLocked && (
                      <span className="text-xs bg-[rgba(255,192,0,0.15)] text-[#1E491A] px-2 py-0.5 rounded-none">Completed</span>
                    )}
                    {isCurrent && !payLocked && (
                      <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-none">Current</span>
                    )}
                    {payLocked && (
                      <span className="text-xs bg-black/5 text-black/40 px-2 py-0.5 rounded-none">Paid</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-black">{stage.name}</h3>
                </div>

                {/* Action Buttons */}
                <div className="flex-shrink-0 flex gap-2">
                  {payLocked ? (
                    <Lock className="h-4 w-4 text-black/20" />
                  ) : isCurrent ? (
                    <>
                      <Button
                        onClick={() => handleStartStage(stage.routeNum)}
                        className="bg-primary text-white hover:bg-primary/90 rounded-none"
                        data-testid={`start-stage-${stage.num}`}
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Start
                      </Button>
                      <Button
                        onClick={() => handleCompleteStage(stage.id)}
                        variant="outline"
                        disabled={actionLoading}
                        className="rounded-none border-green-300 text-[#1E491A] hover:bg-[rgba(255,192,0,0.1)]"
                        data-testid={`complete-stage-${stage.num}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Complete
                      </Button>
                    </>
                  ) : null}
                  {isCompleted && !payLocked && (
                    <>
                      <Button
                        onClick={() => handleStartStage(stage.routeNum)}
                        variant="outline"
                        className="rounded-none"
                        data-testid={`review-stage-${stage.num}`}
                      >
                        Review
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                      <Button
                        onClick={() => handleOpenRestartStage(stage)}
                        variant="ghost"
                        size="sm"
                        className="rounded-none text-black/40 hover:text-black"
                        data-testid={`redo-stage-${stage.num}`}
                      >
                        <RefreshCcw className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {isLocked && !payLocked && (
                    <span className="text-sm text-black/30">Locked</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Continuation Offer for Pathway B/C */}
      {showContinuationOffer && (
        <div
          className="bg-gradient-to-br from-[#1a1a1a] to-[#2a2a2a] rounded-none p-8 border border-[#333] relative overflow-hidden"
          data-testid="continuation-offer"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#F6ADBC]/5 rounded-none -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-none bg-[#F6ADBC] flex items-center justify-center">
                <Rocket className="h-5 w-5 text-[#1E491A]" />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg">Ready for more?</h3>
                <p className="text-white/50 text-sm">{"You've completed your pathway"}</p>
              </div>
            </div>

            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {"You've finished everything in your "}{pathwayName}{". If you want to keep building, you can unlock the Founders Track and get access to "}{upgradeLabel}{" \u2014 the full guided business-building programme."}
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => handleUpgrade(false)}
                disabled={upgradeLoading}
                data-testid="continuation-upgrade-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: '#F6ADBC', color: '#1E491A', border: 'none',
                  padding: '14px 28px', borderRadius: 0,
                  fontSize: '15px', fontWeight: 700,
                  cursor: upgradeLoading ? 'wait' : 'pointer',
                  transition: 'transform 0.15s',
                }}
                onMouseEnter={(e) => { if (!upgradeLoading) e.target.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.target.style.transform = ''; }}
              >
                {upgradeLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>Unlock Founders Track — $TBA</>
                )}
              </button>
              <button
                onClick={() => handleUpgrade(true)}
                data-testid="continuation-demo-bypass-btn"
                style={{
                  background: 'none', border: '1px solid #555', color: '#aaa',
                  padding: '12px 20px', borderRadius: 0,
                  fontSize: '12px', cursor: 'pointer',
                }}
              >
                Skip (Demo)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Congratulations Modal */}
      {showCongrats && completedStageInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-none p-8 max-w-md w-full  text-center">
            <div className="w-16 h-16 bg-[rgba(255,192,0,0.15)] rounded-none flex items-center justify-center mx-auto mb-4">
              <PartyPopper className="h-8 w-8 text-[#1E491A]" />
            </div>
            <h2 className="text-2xl font-bold text-black mb-2">Congratulations!</h2>
            <p className="text-black/60 mb-2">{"You've completed"}</p>
            <p className="text-lg font-semibold text-black mb-6">
              Stage {completedStageInfo.completed?.num}: {completedStageInfo.completed?.name}
            </p>
            
            {completedStageInfo.next ? (
              <>
                <div className="bg-primary/10 rounded-none p-4 mb-6">
                  <p className="text-sm text-black/60 mb-1">Next up:</p>
                  <p className="font-semibold text-black">
                    Stage {completedStageInfo.next.num}: {completedStageInfo.next.name}
                  </p>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setShowCongrats(false)}
                    className="rounded-none"
                  >
                    Stay Here
                  </Button>
                  <Button
                    onClick={handleContinueToNext}
                    className="bg-primary text-white hover:bg-primary/90 rounded-none"
                  >
                    Continue to Next Stage
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-[rgba(255,192,0,0.1)] rounded-none p-4 mb-6">
                  <p className="font-semibold text-[#1E491A]">
                    {"You've completed the entire SPRING programme!"}
                  </p>
                </div>
                <Button
                  onClick={() => setShowCongrats(false)}
                  className="bg-primary text-white hover:bg-primary/90 rounded-none"
                >
                  Close
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Restart Stage Modal */}
      {showRestartStage && selectedStageToRestart && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-none p-6 max-w-md w-full ">
            <div className="flex items-center gap-3 mb-4">
              <RefreshCcw className="h-6 w-6 text-amber-500" />
              <h3 className="text-lg font-semibold text-black">Redo this stage?</h3>
            </div>
            <p className="text-black/60 mb-2">
              Restarting <strong>Stage {selectedStageToRestart.num}: {selectedStageToRestart.name}</strong>
            </p>
            <p className="text-black/60 mb-6 text-sm">
              This will also reset all stages after it, since they build on each other.
              Your account and documents will be kept.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRestartStage(false);
                  setSelectedStageToRestart(null);
                }}
                className="rounded-none"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRestartStage}
                disabled={actionLoading}
                className="bg-amber-500 text-white hover:bg-amber-600 rounded-none"
              >
                {actionLoading ? 'Restarting...' : 'Yes, Redo Stage'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset All Modal */}
      {showResetAll && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-none p-6 max-w-md w-full ">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <h3 className="text-lg font-semibold text-black">Start from the beginning?</h3>
            </div>
            <p className="text-black/60 mb-6">
              This will reset <strong>all your progress</strong> back to Stage 1 (Assessment).
              Your account and saved documents will be kept.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowResetAll(false)}
                className="rounded-none"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetAll}
                disabled={actionLoading}
                className="bg-red-500 text-white hover:bg-red-600 rounded-none"
              >
                {actionLoading ? 'Resetting...' : 'Yes, Start Over'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-secondary/50 rounded-none p-6">
        <h3 className="subhead text-black mb-2">Need guidance?</h3>
        <p className="text-sm text-black/60">
          Your coach Brian remembers everything from your journey and can help you with any stage.
        </p>
      </div>
    </div>
  );
};

export default Stages;
